import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { message } from "antd";
import Layout from "../../Layout";
import HeaderBread from "../../components/HeaderBread";
import SearchField from "../../components/SearchField";
import CustomizedDragNDrop from "../../components/UI/CustomizedDragNDrop";
import ActiveSwitchFile from "../../components/SwitchFile";
import LocationDropdown from "../../components/Menubar/LocationDropdown";
import DeletePop from "../../constants/ErrorPermissionModal";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import { checkPermission } from "../../utils/Validation/checkPermission";
import { addlog } from "../../utils/ApiCalls/Logs";
import {
  getMenubarData,
  deleteMenubarData,
  updateMenubar,
  updateMenubarPosition,
} from "../../utils/ApiCalls/Menubar";

const MODULE_NAME = "Menubar";

const Menubar = () => {
  const navigate = useNavigate();
  const [{ userDetails, permission }, dispatch] = useDataLayerValue();
  const navigation = [
    { title: "Home", route: "/" },
    { title: "Menubar", route: "/website/cms/homepage/menubar" },
  ];

  const [searchParams, setSearchParams] = useSearchParams();
  const [locationId, setLocationId] = useState(() => {
    const param = searchParams.get("location");
    return param ? Number(param) : undefined;
  });
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");

  const getData = async (value = page) => {
    if (!locationId) return;
    dispatch({ type: "SET_LOADING", loading: true });
    let args = { location_id: locationId, page: value, limit: pageSize };
    if (search) args = { ...args, search };
    let data = await getMenubarData(args);
    if (data.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else {
      setRows(data.data || []);
      setCount(data.count || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    getData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, locationId]);

  const handleUpdate = async (id, values, title) => {
    if (!checkPermission(permission, "update_per", MODULE_NAME, window?.location?.pathname)) {
      message.error("Update permission missing");
      return;
    }
    let data = await updateMenubar(id, values);
    if (data?.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else {
      addlog("Menubar Updated", MODULE_NAME, { id, title, ...values }, userDetails.id);
    }
    await getData(page);
  };

  const handleEdit = (id) => {
    if (!checkPermission(permission, "update_per", MODULE_NAME, window?.location?.pathname)) {
      message.error("Update permission missing");
      return;
    }
    navigate("/website/cms/homepage/menubar/edit/" + id);
  };

  const handleDelete = async (id, title) => {
    if (!checkPermission(permission, "delete_per", MODULE_NAME, window?.location?.pathname)) {
      message.error("Missing Permission for delete Menubar");
      return;
    }
    await deleteMenubarData(id);
    addlog("Menubar Deleted", MODULE_NAME, { id, title }, userDetails.id);
    await getData(page);
  };

  const handleLocationChange = (value) => {
    setLocationId(value);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value) params.set("location", value);
        else params.delete("location");
        return params;
      },
      { replace: true }
    );
  };

  const handleManageSubmenu = (id) => {
    navigate(`/website/cms/homepage/menubar/${id}/view`);
  };

  const updateMenubarPositionHandler = async (updatedRows) => {
    if (!checkPermission(permission, "update_per", MODULE_NAME, window?.location?.pathname)) {
      message.error("Update permission missing");
      return;
    }
    dispatch({ type: "SET_LOADING", loading: true });
    // CustomizedDragNDrop computes row.position for a descending-sorted list
    // (top row = highest number); this list is sorted ascending by order_by,
    // so derive the value from each row's new index instead of using it as-is.
    const items = updatedRows.map((row, index) => ({
      id: row.id,
      orderBy: index + 1 + (page - 1) * pageSize,
    }));
    let data = await updateMenubarPosition(items);
    if (data?.error) {
      Swal.fire({ icon: "error", title: "Oops...", text: data.error });
    } else {
      addlog("Menubar Position Updated", MODULE_NAME, { locationId }, userDetails.id);
    }
    await getData(page);
  };

  const columns = [
    {
      id: "id",
      title: "Sl",
      width: 30,
      dataIndex: "id",
      key: "id",
      render: (text, record, index) => (page - 1) * pageSize + index + 1,
    },
    {
      id: "menuName",
      title: "Menu Name",
      dataIndex: "menuName",
      key: "menuName",
      width: 150,
    },
    {
      id: "url",
      title: "URL / Slug",
      dataIndex: "url",
      key: "url",
      width: 200,
      render: (url) => <p>{url || <em>(home)</em>}</p>,
    },
    {
      id: "is_active",
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (item, record, index) => (
        <ActiveSwitchFile
          module={MODULE_NAME}
          item={item}
          record={record}
          index={index}
          handleUpdate={(id, values, title) => handleUpdate(id, values, title)}
        />
      ),
    },
    {
      id: "operation",
      title: "Action",
      key: "operation",
      width: 150,
      render: (text, record) => (
        <div className="table_action_btn">
          <button className="btn" onClick={() => handleEdit(record.id)}>
            <i className="ri-edit-2-line" data-toggle="tooltip" title="Edit"></i>
          </button>
          <button className="btn" onClick={() => handleManageSubmenu(record.id)}>
            <i className="ri-list-check" data-toggle="tooltip" title="Manage Sub-menu"></i>
          </button>
          <DeletePop onConfirm={() => handleDelete(record.id, record.menuName)}>
            <div>
              <button className="btn delete_btn">
                <i className="ri-delete-bin-line" data-toggle="tooltip" title="Delete"></i>
              </button>
            </div>
          </DeletePop>
        </div>
      ),
    },
  ];

  return (
    <Layout type={"read_per"} module_data={MODULE_NAME}>
      <div>
        <HeaderBread navigation={navigation} title={"Menubar"}></HeaderBread>
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">
            <div className="card">
              <div className="card-header two_item">
                <h5 className="card-title">Menubar List</h5>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "5px",
                    justifyContent: "flex-end",
                  }}
                >
                  <LocationDropdown value={locationId} onChange={handleLocationChange} width={200} />
                  <Link to="/website/cms/homepage/menubar/add" className="btn add_v_btn">
                    Add Menu
                  </Link>
                </div>
              </div>
              <div className="card-body">
                <div className="table_holder">
                  <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                    <div style={{ maxWidth: 200 }}>
                      <SearchField
                        value={search}
                        SearchValue={() => setPage(1)}
                        setValue={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  {rows && (
                    <CustomizedDragNDrop
                      setRows={setRows}
                      setPageSize={setPageSize}
                      setPage={setPage}
                      pageSize={pageSize}
                      page={page}
                      count={count}
                      columns={columns}
                      rows={rows}
                      sortHandler={() => {}}
                      updatePosition={updateMenubarPositionHandler}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Menubar;
